// ABOUTME: Shared axios client, base URL, interceptors, and response/error wrappers
// ABOUTME: Used by all domain-specific API modules in src/lib/api

import type { ApiErrorResponse } from '@alpha-finder/contracts/envelope';
import type { BrowserAuth } from '@boundarylab/auth/browser';
import axios, { type AxiosInstance } from 'axios';
import { auth, currentToken, SKIP_AUTH } from '../auth-client';

// Base URL without /api/v1 suffix - used for health checks and WebSocket connections
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:10001';

// Full API URL with version prefix
const VERSIONED_API_URL = `${API_BASE_URL}/api/v1`;

export const apiClient = axios.create({
  baseURL: VERSIONED_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Attaches the request (X-Request-ID + bearer) and 401-recovery response
 * interceptors to an axios instance. Exported so it can be exercised against a
 * throwaway instance in tests without depending on the shared singleton.
 *
 * The bearer is the blab-auth app token from `currentToken()`, which reuses the
 * stored token while it is live and mints a new one from the blab-auth session
 * otherwise; an empty token (local SKIP_AUTH) sends no Authorization header. When
 * there is no session, `currentToken()` rejects with NotSignedInError and tells
 * AuthGate, so the request is never sent.
 *
 * 401 recovery: a token the api rejects (revoked, or expired between the check and
 * the request) is replaced through `auth.refresh()` and the request is retried
 * once. When the session is gone, or the fresh token is rejected too, an
 * `auth:unauthorized` event tells AuthGate to re-check the session (an event, not
 * a hard redirect, so React Router handles navigation). Under `skipAuth` (local
 * Workers that accept unauthenticated requests) there is no session to refresh.
 */
export function attachAuthInterceptors(
  client: AxiosInstance,
  a: BrowserAuth = auth,
  tokenFn: () => Promise<string> = currentToken,
  skipAuth: boolean = SKIP_AUTH
): void {
  client.interceptors.request.use(async (config) => {
    config.headers['X-Request-ID'] = crypto.randomUUID();
    const token = await tokenFn();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      if (error.response?.status === 401 && original && !original._retriedAuth && !skipAuth) {
        original._retriedAuth = true;
        const session = await a.refresh();
        if (session.status === 'signed_in') return client(original);
      }
      if (error.response?.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      throw error;
    }
  );
}

attachAuthInterceptors(apiClient);

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function getErrorMessage(response: ApiErrorResponse | undefined): string {
  if (!response) return 'An unexpected error occurred';
  return response.error?.message ?? 'An unexpected error occurred';
}

// The `{ code, details }` of an api error envelope, or null for anything else.
function errorCodeAndDetails(
  err: unknown
): { code: unknown; details: Record<string, unknown> } | null {
  if (!axios.isAxiosError(err)) return null;
  const body = err.response?.data;
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const e = (body as { error: unknown }).error;
  if (typeof e !== 'object' || e === null) return null;
  const errObj = e as { code?: unknown; details?: unknown };
  const details = errObj.details;
  if (typeof details !== 'object' || details === null) return null;
  return { code: errObj.code, details: details as Record<string, unknown> };
}

const stringsIn = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

// Symbol-registration error discriminator. The api emits a structured
// VALIDATION_ERROR with details.reason='not-registered' (or 'invalid-format')
// when a strict gate rejects an unregistered/malformed symbol — see
// docs/superpowers/specs/2026-06-12-symbol-registration-adr.md §5.2.
// `suggestions` lists registered listings of the same base (DG.PA for DG).
export interface SymbolNotRegisteredApiError {
  kind: 'symbol-not-registered';
  symbol: string;
  suggestion?: string;
  suggestions: string[];
}

export function asSymbolNotRegisteredError(err: unknown): SymbolNotRegisteredApiError | null {
  const parsed = errorCodeAndDetails(err);
  if (parsed?.code !== 'VALIDATION_ERROR') return null;
  const d = parsed.details;
  if (d.reason !== 'not-registered') return null;
  if (typeof d.symbol !== 'string') return null;
  return {
    kind: 'symbol-not-registered',
    symbol: d.symbol,
    suggestion: typeof d.suggestion === 'string' ? d.suggestion : undefined,
    suggestions: stringsIn(d.suggestions),
  };
}

// Registration's answer for a symbol Yahoo Finance doesn't list: NOT_FOUND with
// details.reason='unknown-symbol' and Yahoo's listings of the same base. A
// non-admin's register call also answers NOT_FOUND, without these details.
export interface UnknownSymbolApiError {
  kind: 'unknown-symbol';
  symbol: string;
  suggestions: string[];
}

export function asUnknownSymbolError(err: unknown): UnknownSymbolApiError | null {
  const parsed = errorCodeAndDetails(err);
  if (parsed?.code !== 'NOT_FOUND') return null;
  const d = parsed.details;
  if (d.reason !== 'unknown-symbol' || typeof d.symbol !== 'string') return null;
  return { kind: 'unknown-symbol', symbol: d.symbol, suggestions: stringsIn(d.suggestions) };
}
